// Opening a work order in the list: selectWorkOrder / displayOperations (its operations panel,
// balance and buttons), removeOperationDetails, the work order's invoice preview, and the DockMaster
// work order and invoice calls (processInvoices, updateWorkOrderDM, deleteOperationDM).
// Moved out of app.js unchanged (docs/split-plan.md, session 7c).
import { paintLine } from './bottomPaint';
import { openAddOperationModal } from './addOperation';
import { parseDay } from './date-format';
import { openInvoiceDocument } from './estimates';
import { auth } from './initFirebase';
import { invoiceWorkOrder } from './invoices';
import { getTechId } from './jobWrites';
import { usesDockmaster } from './marinaConfig';
import { editWorkOrderData } from './opActions';
import { showPage } from './shellPages';
import { techsLabel } from './techNames';
import { deleteDMOperationProxy, escapeHTML, fetchDMWorkOrder, fetchOperationsForWorkOrder, getCustomerBalance, getCustomerDMID, isAdminRole, toastMessage, updateDMWorkOrder } from './utilities';
import { aggregateWorkOrder, techAvatar } from './wo-ops-aggregation';
import { opProgressHeaderHtml, opSummaryChipsHtml, opTableList } from './woTable';

let shell = null;
export function setWoExpandDeps(deps) { shell = deps; }

// Boat name for the customer whose balance is currently shown in #invoiceModal —
// captured alongside #invoice-customer since processInvoices()/getCustomerBalance()
// only returns DockMaster's balance payload, which has no boat field.
let currentInvoiceBoatName = '';
let currentInvoiceCustomerId = ''; // for Email on the invoice document

async function processInvoices(result){

  // #balance-amount is a button recreated fresh every time displayOperations()
  // (re)builds the WO panel — if that panel gets closed/reopened (or, as of the
  // op-add live-refresh fix, rebuilt right after adding an operation) while an
  // earlier collectBalance() call for the *previous* render is still in flight,
  // its #balance-amount lookup can land in the gap between the old button being
  // removed and the new one being appended. Guard rather than assume it's there.
  if(!result){
    const balanceEl = document.getElementById('balance-amount');
    if (balanceEl) balanceEl.innerHTML = `Current Balance: $0.00`;
    return null;
  };

  const invoices = result.Invoices;

    const rows = [];

    for (const inv of invoices) {

      if(!inv.Description.includes('WO Billing')){
        continue;
      }

      const tbody = document.getElementById('invoice-body');
      if (!tbody) continue;
      tbody.innerHTML = '';

      if (inv.Description.split(' ')[0] !== '') {
        const workOrderCheck = inv.Description.split(' ')[0];
        const workOrderData = await getWorkOrderInfoDM(workOrderCheck, inv.DueDate, inv.Id);

        if(workOrderData.length === 0) {
          return
        }

        for (const r of workOrderData) {
          const row = document.createElement('tr');
          const invoiceNumber = document.createElement('td');
          invoiceNumber.textContent = inv.Id;
          const workOrder = document.createElement('td');
          workOrder.innerHTML = r.Work_Order;
          const description = document.createElement('td');
          description.textContent = r.Description.split(':')[0];
          description.style.fontSize = '.75rem';
          const date = document.createElement('td');
          date.innerHTML = r.Bill_Date;

          const startDate = parseDay(r.Bill_Date);
          const endDate = new Date();
          const differenceInMilliseconds = endDate - startDate;
          const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));

          const pastDue = document.createElement('td');
          pastDue.innerHTML = differenceInDays;
          const amount = document.createElement('td');
          amount.innerHTML = r.Charge;

          const printCell = document.createElement('td');
          const printButton = document.createElement('button');
          printButton.type = 'button';
          printButton.className = 'invoice-print-row-button';
          printButton.title = 'Print / PDF';
          printButton.innerHTML = '<img src="/assets/print.png" alt="Print" width="16" height="16">';
          printButton.addEventListener('click', (e) => {
            e.stopPropagation();
            openInvoiceDocument({
              Id: inv.Id,
              DueDate: inv.DueDate,
              InvoiceAmount: inv.InvoiceAmount,
              DaysPastDue: differenceInDays,
              WorkOrder: r.Work_Order,
              CustomerName: document.getElementById('invoice-customer')?.textContent || '',
              CustomerId: currentInvoiceCustomerId,
              BoatName: currentInvoiceBoatName,
              LineItems: workOrderData
            });
          });
          printCell.appendChild(printButton);

          row.appendChild(invoiceNumber);
          row.appendChild(workOrder);
          row.appendChild(description);
          row.appendChild(date);
          row.appendChild(pastDue);
          row.appendChild(amount);
          row.appendChild(printCell);

          rows.push(row);
        }
      } else {
        const row = document.createElement('tr');
        const invoiceNumber = document.createElement('td');
        invoiceNumber.textContent = inv.Id;
        const workOrder = document.createElement('td');
        workOrder.innerHTML = "";
        const description = document.createElement('td');
        description.innerHTML = 'Point of Sale';
        const date = document.createElement('td');
        date.innerHTML = inv.DueDate;

        const startDate = parseDay(inv.DueDate);
        const endDate = new Date();
        const differenceInMilliseconds = endDate - startDate;
        const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));

        const pastDue = document.createElement('td');
        pastDue.innerHTML = differenceInDays;
        const amount = document.createElement('td');
        amount.innerHTML = `$${Number(inv.InvoiceAmount).toFixed(2)}`;

        const printCell = document.createElement('td');
        const printButton = document.createElement('button');
        printButton.type = 'button';
        printButton.className = 'invoice-print-row-button';
        printButton.title = 'Print / PDF';
        printButton.innerHTML = '<img src="/assets/print.png" alt="Print" width="16" height="16">';
        printButton.addEventListener('click', (e) => {
          e.stopPropagation();
          openInvoiceDocument({
            Id: inv.Id,
            Description: 'Point of Sale',
            DueDate: inv.DueDate,
            InvoiceAmount: inv.InvoiceAmount,
            DaysPastDue: differenceInDays,
            WorkOrder: '',
            CustomerName: document.getElementById('invoice-customer')?.textContent || '',
            CustomerId: currentInvoiceCustomerId,
            BoatName: currentInvoiceBoatName,
            LineItems: []
          });
        });
        printCell.appendChild(printButton);

        row.appendChild(invoiceNumber);
        row.appendChild(workOrder);
        row.appendChild(description);
        row.appendChild(date);
        row.appendChild(pastDue);
        row.appendChild(amount);
        row.appendChild(printCell);

        rows.push(row);
      }
    }

    // Sort rows by date
    rows.sort((a, b) => {
      const dateA = new Date(a.querySelector('td:nth-child(4)').innerText);
      const dateB = new Date(b.querySelector('td:nth-child(4)').innerText);
      return dateA - dateB;
    });

    // Append sorted rows to the table
    const tbody = document.getElementById('invoice-body');
    if (tbody) rows.forEach(row => tbody.appendChild(row));
  

  let balance = result.Balance;

  // Balance can legitimately be missing (e.g. this customer has no synced Dockmaster balance) —
  // treat that the same as a zero balance instead of throwing before the $0.00 fallback below
  // ever gets a chance to run.
  let numStr = (balance === undefined || balance === null) ? '0' : balance.toString();

  if (numStr.length < 3) {
    while (numStr.length < 3) {
      numStr = '0' + numStr;
    }
  }

  let formattedNumber = numStr.slice(0, -2) + '.' + numStr.slice(-2);
  const balanceEl = document.getElementById('balance-amount');
  if (balanceEl) {
    balanceEl.innerHTML = formattedNumber
      ? `Current Balance: $${Number(formattedNumber).toFixed(2).toLocaleString()}`
      : `Current Balance: $0.00`;
  }
}


async function getWorkOrderInfoDM(workOrder, billDate, invoiceNumber) {
  const result = await fetchDMWorkOrder(workOrder, billDate, invoiceNumber);
  if (!result || !result.Operations || result.Operations.length === 0) return;
  const data = [];
  result.Operations.forEach(op => {
    const info = {
      Charge: `$${Number(op.TotalCharges).toFixed(2).toLocaleString()}`,
      Description: op.LongDesc,
      Work_Order: workOrder,
      Bill_Date: billDate,
      Invoice: invoiceNumber
    };
    data.push(info);
  });
  return data;
}

export async function updateWorkOrderDM(serviceLog) {
  const payload = {
    CustId: serviceLog.DMCustomer,
    WOId: serviceLog.Work_Order,
    Type: serviceLog.type,
    LocationCode: "1",
    ClerkId: serviceLog.clerkId,
    OperationCodes: [{
      ShortDesc: serviceLog.Short_Description_DM ? serviceLog.Short_Description_DM : serviceLog.Short_Description,
      Opcode: serviceLog.OpCode,
      LongDesc: serviceLog.Description,
      TechDesc: serviceLog.Description,
      Desc: serviceLog.Short_Description_DM ? serviceLog.Short_Description_DM : serviceLog.Short_Description,
    }]
  };
  if (serviceLog.flatLabor || serviceLog.flatPerFoot || serviceLog.flatMethod) {
    if (serviceLog.flatLabor !== '') payload.OperationCodes[0].FlatRateAmount = serviceLog.flatLabor.toString();
    if (serviceLog.flatPerFoot !== '') payload.OperationCodes[0].FlatRatePerFootRate = serviceLog.flatPerFoot.toString();
    if (serviceLog.flatMethod !== '') payload.OperationCodes[0].FlatRatePerFootMethod = serviceLog.flatMethod.toString();
  }
  if (serviceLog.Title) {
    payload.Title = serviceLog.Title;
  } else {
    payload.Title = serviceLog.Short_Description;
  }
  const result = await updateDMWorkOrder(payload, false);
  return result;
}


export async function deleteOperationDM(WOId, Operation) {
  const result = await deleteDMOperationProxy(WOId, Operation);
  return result;
}


document.addEventListener('click', (e) => {
  if (!shell.activeRow || !shell.activeRow.isConnected) {
    shell.activeRow = null;
    document.querySelector('.operation-container-row')?.remove();
    return;
  }
  if ((e.target.id === 'table-content' || e.target.id === 'search') && shell.activeRow) {
    removeOperationDetails(shell.activeRow);
  }
});


export async function selectWorkOrder(e) {

  const clickedRow = e.target.closest('.table-item');
  const tableContent = document.getElementById('table-content');

  if (!clickedRow) return;

  const data = clickedRow;
  shell.activeTableRow = data;
  const info = JSON.parse(data.getAttribute('data-jobInfo'));
  const jobId = data.getAttribute('data-id');
  const customerId = data.getAttribute('data-customerId');
  const boatId = data.getAttribute('data-boatId');
  const workOrder = data.getAttribute('data-workorder');

  shell.globalWorkOrder = workOrder;
  document.getElementById('activeWorkOrder').value = workOrder;
  tableContent.style.padding = '0rem';

  if (clickedRow === shell.activeRow && !isIgnoredElement(e.target) && shell.activeRow) {
    // Remove the operation details container if the same row is clicked again
    removeOperationDetails(clickedRow);
    document.querySelector('.active-work-order')?.classList.remove('active-work-order');
    updateWoDiscloseLabel(clickedRow);
  } else if (!isIgnoredElement(e.target)) {

    const previousRow = shell.activeRow;
    if (shell.activeRow) removeOperationDetails(shell.activeRow);

    shell.activeRow = clickedRow;
    shell.activeTableRow = shell.activeRow;
    document.querySelector('.active-work-order')?.classList.remove('active-work-order');
    shell.activeRow.classList.add('active-work-order');
    updateWoDiscloseLabel(previousRow);
    updateWoDiscloseLabel(shell.activeRow);

    displayOperations(shell.globalWorkOrder, clickedRow); // Display the operations related to the work order
  }
}

// Keeps the Status-column disclosure text ("N operations ⌄" / "Hide
// operations ⌃") in sync with whether this row's expand panel is open.
function updateWoDiscloseLabel(row) {
  if (!row) return;
  const disclose = row.querySelector('.wo-disclose');
  if (!disclose) return;
  if (row.classList.contains('active-work-order')) {
    disclose.textContent = 'Hide operations ⌃';
    return;
  }
  const wo = row.getAttribute('data-workorder');
  const ops = (shell.jobsByWorkOrder && shell.jobsByWorkOrder.get(wo)) || [];
  const count = ops.filter(op => op.Status?.toString() !== 'Declined').length;
  disclose.textContent = `${count} operation${count === 1 ? '' : 's'} ⌄`;
}

export function removeOperationDetails(row) {

  const host =
    (row && row.isConnected && row) ||
    (shell.activeRow && shell.activeRow.isConnected && shell.activeRow) ||
    null;

  let containerRow = null;
  if (host && host.nextElementSibling && host.nextElementSibling.classList.contains('operation-container-row')) {
    containerRow = host.nextElementSibling;
  } else {
    containerRow = document.querySelector('.operation-container-row');
  }

  if (containerRow && containerRow.parentNode) {
    if (containerRow._scrollHandler) {
      document.getElementById('table-content')?.removeEventListener('scroll', containerRow._scrollHandler);
    }
    containerRow.parentNode.removeChild(containerRow);
  }

  if (host && host.classList) host.classList.remove('selectedRow');
  shell.activeRow = null;
}


function isIgnoredElement(target) {
  return target.tagName === 'BUTTON' || target.id === 'search' || target.id === 'table-content';
}

export async function displayOperations(workOrderNumber, afterRow) {

  if (!afterRow || !afterRow.isConnected || !afterRow.parentNode) {
  removeOperationDetails();
  return;
}


  const info = JSON.parse(afterRow.getAttribute('data-jobInfo'));

  document.getElementById('invoice-body').innerHTML = '';

  let currentUser = auth.currentUser;
  // Use the role already fetched at login — avoids a live Firebase read on every click
  let hasAccess = shell.userRole || localStorage.getItem('currentUserRole');

  const balanceButton = document.createElement('button')
  balanceButton.classList.add('hide-mobile');


  // Use the in-memory map when available (built at load time), fall back to IDB scan
  const opsPromise = (shell.jobsByWorkOrder && shell.jobsByWorkOrder.has(workOrderNumber))
    ? Promise.resolve(shell.jobsByWorkOrder.get(workOrderNumber))
    : fetchOperationsForWorkOrder(workOrderNumber);

  opsPromise
    .then(async operations => {

      // Check if operations are available
      if (!operations || operations.length === 0) {
        console.log('No operations found for this work order.');
        return; // Exit if no operations
      }

        const existingContainer = afterRow.parentNode?.querySelector('.operation-container');
        if (existingContainer) existingContainer.remove();

        if (afterRow.nextElementSibling && afterRow.nextElementSibling.classList.contains('operation-container-row')) {
          afterRow.parentNode?.removeChild(afterRow.nextElementSibling);
          return;
        }


      // Create a new table row to host the operations container
      let containerRow = document.createElement('div');
      containerRow.className = 'operation-container-row'; // For potential styling or identification
      // Tags which WO row this panel belongs to — performSort() re-anchors
      // open panels after resorting #table-content by this attribute (see
      // reattachOperationPanels()), since this row is a plain DOM sibling of
      // its .table-item, not a child, and sorting only knows how to move
      // .table-item elements.
      containerRow.setAttribute('data-workorder', workOrderNumber);

      let buttonRow = document.createElement('div');
      buttonRow.className = 'operation-button-row';

      // Create a table cell that spans all columns
      let containerCell = document.createElement('div');
      containerCell.colSpan = afterRow.children.length; // Set colspan to match the number of columns in the parent table
      containerCell.className = 'operation-div'

      // Create a container div for the operations table
      let containerDiv = document.createElement('div');
      containerDiv.style.width = '100%'; // Ensure it spans the full width
      containerDiv.className = 'operation-container'; // For styling

      // Progress summary header — "X hrs logged · Y of Z done", computed from
      // data already on hand (each op's Labor entries + Status), nothing new
      // to fetch or wire. Shares aggregateWorkOrder() with the collapsed
      // row's progress bar so the two never disagree.
      const agg = aggregateWorkOrder(operations);
      const activeOps = agg.activeOps;

      const progressHeader = document.createElement('div');
      progressHeader.className = 'op-progress-header';
      progressHeader.innerHTML = opProgressHeaderHtml(agg);

      // Summary chips — the per-status counts the collapsed row's bar no
      // longer spells out (design-files §3a expanded row).
      const summaryChips = document.createElement('div');
      summaryChips.className = 'op-summary-chips';
      summaryChips.innerHTML = opSummaryChipsHtml(agg);

      const opsColumn = document.createElement('div');
      opsColumn.className = 'op-ops-column';
      opsColumn.appendChild(progressHeader);
      opsColumn.appendChild(summaryChips);

      // Create the operations table within the div
      const operationsTable = document.createElement('div');
      operationsTable.id = 'operations-table'; // Add styling as needed

      opTableList(operations, operationsTable);
      opsColumn.appendChild(operationsTable);

      // Customer & Boat sidebar — built from the same boat record the
      // Haul/Launch and Dashboard screens already treat as source of truth.
      // Labor/Parts cost totals are intentionally NOT shown here: neither
      // Labor entries nor Parts entries carry a $ rate anywhere in the data
      // model today, so a cost card would have to invent numbers.
      // Collapsed by default (a "Customer & Boat ▸" toggle chip) so it
      // doesn't compete with the operations table for width/height on open —
      // the customer's already named on the row above this panel.
      const sidebarColumn = document.createElement('div');
      sidebarColumn.className = 'op-sidebar-column';
      try {
        const cb = await shell.getCustomerDataById(info.Customer_ID, info.Boat_ID, info);
        const boatData = cb?.boatData;
        const custData = cb?.customerData;
        if (boatData || custData) {
          const engineKey = boatData?.Engines ? Object.keys(boatData.Engines)[0] : null;
          const engine = engineKey ? boatData.Engines[engineKey] : null;
          const engineLabel = engine ? [engine.Engine_Make, engine.Engine_Model].filter(Boolean).join(' ') : '';
          const sidebarTechNames = [...new Set(activeOps.map(o => o.Techs).filter(t => t && t !== 'Unassigned'))];
          const sidebarTechChips = sidebarTechNames.map(name => {
            const { initials, color } = techAvatar(name, getTechId);
            return `<span class="op-sidebar-tech-chip"><span class="op-sidebar-tech-avatar" style="background:${color};">${escapeHTML(initials)}</span>${escapeHTML(techsLabel(name))}</span>`;
          }).join('');
          const phone = custData?.Phone || '';
          const phoneHref = phone.replace(/[^\d+]/g, '');

          sidebarColumn.innerHTML = `
            <button type="button" class="op-sidebar-toggle">
              <span class="op-sidebar-toggle-label">Customer &amp; Boat</span>
              <span class="op-sidebar-toggle-icon">&#9656;</span>
            </button>
            <div class="op-sidebar-content">
              <div class="op-sidebar-section">
                <div class="op-sidebar-name">${escapeHTML(custData?.Customer_Name || info.Customer_Name || '')}</div>
                ${phone ? `<a class="op-sidebar-phone" href="tel:${escapeHTML(phoneHref)}">${escapeHTML(phone)}</a>` : ''}
                <div class="op-sidebar-section-label">Boat</div>
                <div class="op-sidebar-grid">
                  <div><div class="op-sidebar-field-label">LOA / Beam</div><div class="op-sidebar-field-value">${escapeHTML([boatData?.Boat_Length, boatData?.Boat_Beam].filter(Boolean).join(' · ') || '—')}</div></div>
                  <div><div class="op-sidebar-field-label">Engine</div><div class="op-sidebar-field-value">${escapeHTML(engineLabel || '—')}</div></div>
                  <div><div class="op-sidebar-field-label">Mooring</div><div class="op-sidebar-field-value">${escapeHTML(boatData?.Mooring_Number || '—')}</div></div>
                  <div><div class="op-sidebar-field-label">Bottom</div><div class="op-sidebar-field-value">${escapeHTML(paintLine(boatData))}</div></div>
                </div>
              </div>
              <div class="op-sidebar-divider"></div>
              <div class="op-sidebar-section-label">Season</div>
              <div class="op-sidebar-dates">
                <div><div class="op-sidebar-field-label">Haul</div><div class="op-sidebar-date-value">${escapeHTML(boatData?.Haul_Date || '—')}</div></div>
                <div><div class="op-sidebar-field-label">Launch</div><div class="op-sidebar-date-value">${escapeHTML(boatData?.Launch_Date || '—')}</div></div>
              </div>
              ${sidebarTechChips ? `<div class="op-sidebar-divider"></div><div class="op-sidebar-tech"><div class="op-sidebar-field-label">Assigned tech</div><div class="op-sidebar-tech-chips">${sidebarTechChips}</div></div>` : ''}
            </div>
          `;
          sidebarColumn.querySelector('.op-sidebar-toggle').addEventListener('click', (e) => {
            e.stopPropagation();
            sidebarColumn.classList.toggle('op-sidebar-open');
          });
        }
      } catch (err) {
        console.warn('Could not load Customer & Boat sidebar data:', err);
      }

      // Another row was opened (or this one closed) while the sidebar lookup was in flight.
      if (shell.activeRow !== afterRow || !afterRow.isConnected) return;

      const checkOverallStatus = operations.every(op => /* op.Status === 'Closed' || */ op.Status === 'Declined');

      if (checkOverallStatus) {
        removeOperationDetails(afterRow);
        return;
      }


      if (hasAccess && isAdminRole(hasAccess) && shell.activeRow != null) {

        balanceButton.id = 'balance-amount';
        balanceButton.classList.add('dm-only');
        document.getElementById('invoice-customer').textContent = info.Customer_Name;
        currentInvoiceBoatName = info.Boat_Name || '';

        balanceButton.addEventListener('click', (e) => {
          console.log('Customer Balance Clicked')
          e.stopPropagation();
          document.getElementById('invoiceModal').style.display = 'flex';
        })

        balanceButton.style.display = "flex";
        balanceButton.innerHTML = 'Customer Balance: $--.--';
      }

      const addOpButton = document.createElement('button');
      addOpButton.addEventListener('click', () => openAddOperationModal());
      addOpButton.className = 'add-op-button';
      addOpButton.innerHTML = 'Add Operation';

      const invoiceButton = document.createElement('button');
      invoiceButton.className = 'customer-record-button';
      invoiceButton.classList.add('hide-mobile', 'dm-only');
      invoiceButton.innerHTML = 'Invoice';
      invoiceButton.addEventListener('click', async (e) => {
        e.stopPropagation();
        await openWoInvoicePreview(workOrderNumber, info.Customer_ID, info.Customer_Name, info.Boat_Name);
      });

      // The app's own billing: a draft invoice from this work order's billed operations.
      const createInvoiceButton = document.createElement('button');
      createInvoiceButton.className = 'customer-record-button';
      createInvoiceButton.classList.add('hide-mobile', 'app-billing-only');
      createInvoiceButton.innerHTML = 'Create invoice';
      createInvoiceButton.addEventListener('click', async (e) => {
        e.stopPropagation();
        await invoiceWorkOrder(workOrderNumber, info.Customer_ID);
      });

      const customerRecordButton = document.createElement('button');
      customerRecordButton.addEventListener('click', async (e) => {
        e.stopPropagation();

        const customerIframe = document.getElementById('customers');

        if(!customerIframe.querySelector('iframe')){
          await showPage('customers')
            .then(() => shell.callOpenCustomerData(info.Customer_ID, 'workOrder'))
            .catch(console.error);;
        }else if( customerIframe.querySelector('iframe')){
          shell.callOpenCustomerData(info.Customer_ID, 'workOrder');
        }

      });

      customerRecordButton.className = 'customer-record-button';
      customerRecordButton.classList.add('hide-mobile');
      customerRecordButton.innerHTML = 'Customer Record';

      const workOrderEditButton = document.createElement('button');
      workOrderEditButton.id = 'edit-work-order-button';
      workOrderEditButton.addEventListener('click', (e) => {
        editWorkOrderData();
      });
      workOrderEditButton.className = 'customer-record-button';
      workOrderEditButton.classList.add('hide-mobile');
      workOrderEditButton.innerHTML = 'Edit Work Order' + '<img src="/assets/edit_icon.png" alt="Edit Icon" width="18" height="18">';

      const closeWorkOrderButton = document.createElement('button');
      closeWorkOrderButton.addEventListener('click', (e) => {
        e.stopPropagation();
        const operationData = []
        const operations = document.querySelectorAll('.op-table-data-row');
        operations.forEach(op => {
          operationData.push(JSON.parse(op.getAttribute('data-jobinfo')));
          });
          console.log(operationData);
          
        });

      closeWorkOrderButton.className = 'close-work-order-button';
      closeWorkOrderButton.classList.add('hide-mobile');
      closeWorkOrderButton.innerHTML = 'Close Work Order' + '<img class="icon-white" src="/assets/close-circle.png" alt="Edit Icon" width="18" height="18">';

      // "Schedule the unassigned op" — jumps to and opens the date picker on
      // the first operation missing a Schedule_Date/Techs, rather than making
      // the manager hunt for it in a 41-row expanded panel.
      const scheduleUnassignedButton = document.createElement('button');
      scheduleUnassignedButton.className = 'customer-record-button';
      scheduleUnassignedButton.classList.add('hide-mobile');
      scheduleUnassignedButton.innerHTML = 'Schedule the unassigned op';
      scheduleUnassignedButton.addEventListener('click', (e) => {
        e.stopPropagation();
        const unscheduledRow = operationsTable.querySelector('.op-row-unscheduled');
        if (!unscheduledRow) {
          toastMessage('Nothing unscheduled on this work order.', 'green');
          return;
        }
        unscheduledRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const dateInput = unscheduledRow.querySelector('.schedule-date-info');
        if (dateInput) { dateInput.focus(); if (typeof dateInput.showPicker === 'function') dateInput.showPicker(); }
      });

      buttonRow.appendChild(balanceButton);
      buttonRow.appendChild(addOpButton);
      buttonRow.appendChild(scheduleUnassignedButton);
      buttonRow.appendChild(customerRecordButton);
      if (hasAccess && isAdminRole(hasAccess) && shell.activeRow != null) {
        buttonRow.appendChild(invoiceButton);
      }
      if (hasAccess && /admin|office/i.test(String(hasAccess)) && shell.activeRow != null) {
        buttonRow.appendChild(createInvoiceButton);
      }

      if(checkOverallStatus){
        buttonRow.appendChild(closeWorkOrderButton);
      }else{
        buttonRow.appendChild(workOrderEditButton);
      }

      containerRow.appendChild(buttonRow);

      containerCell.appendChild(containerDiv);
      containerDiv.appendChild(opsColumn);
      containerDiv.appendChild(sidebarColumn);
      containerRow.appendChild(containerCell);

      afterRow.parentNode.insertBefore(containerRow, afterRow.nextSibling);

      const tableContent = document.getElementById('table-content');
      if (tableContent) {
        // Scroll now that the operations panel is in the DOM.
        const rowRect   = afterRow.getBoundingClientRect();
        const tableRect = tableContent.getBoundingClientRect();
        tableContent.scrollTo({
          top: tableContent.scrollTop + (rowRect.top - tableRect.top),
          behavior: 'smooth'
        });

        // Auto-close when the work-order row scrolls out of view.
        const handleScroll = () => {
          if (!afterRow.isConnected) {
            tableContent.removeEventListener('scroll', handleScroll);
            return;
          }
          const rRect = afterRow.getBoundingClientRect();
          const tRect = tableContent.getBoundingClientRect();
          if (rRect.bottom < tRect.top || rRect.top > tRect.bottom) {
            tableContent.removeEventListener('scroll', handleScroll);
            removeOperationDetails(afterRow);
            document.querySelector('.active-work-order')?.classList.remove('active-work-order');
            updateWoDiscloseLabel(afterRow);
          }
        };
        tableContent.addEventListener('scroll', handleScroll);
        containerRow._scrollHandler = handleScroll;
      }

      if(navigator.onLine && usesDockmaster()){
      await collectBalance(info.Customer_ID);
      }

    })
    .catch(error => {
      console.error("Failed to fetch operations:", error);
    });

}

document.getElementById('customerRecordMobile').addEventListener('click', async (e) => {

  e.stopPropagation();

  const customerId = document.getElementById('updateCustomerId').value;

  const customerIframe = document.getElementById('customers');

  if (!customerIframe.querySelector('iframe')) {
    await showPage('customers')
      .then(() => shell.callOpenCustomerData(customerId, 'workOrder'))
      .catch(console.error);;
  } else if (customerIframe.querySelector('iframe')) {
    shell.callOpenCustomerData(customerId, 'workOrder');
  }

});

async function collectBalance(customerId) {
  currentInvoiceCustomerId = customerId;
  const customerDM = await getCustomerDMID(customerId);
  await getCustomerBalance(customerDM).then((result) => {processInvoices(result)});
}

// Real WO-scoped invoice preview, sourced from DockMaster (same data path as
// collectBalance/processInvoices, just filtered to one work order instead of
// the customer's whole balance). There is no "create/send invoice" capability
// anywhere in this codebase today — invoices are created directly in
// DockMaster, outside this app — so this is read-only: it shows the matching
// DockMaster invoice's real line items and total if one exists, or an honest
// "not yet invoiced" state if it doesn't. No totals are fabricated.
// Shared DockMaster invoice lookup — used by openWoInvoicePreview() and the
// billing surface's reconciliation panel (design_handoff_billing §5), which
// explicitly asks for this to be one function rather than a second copy.
// Read-only: never writes anything back to DockMaster.
export async function lookupDockmasterInvoiceForWorkOrder(workOrder, customerId) {
  const customerDM = await getCustomerDMID(customerId);
  const balance = await getCustomerBalance(customerDM);
  const invoices = balance?.Invoices || [];
  const match = invoices.find(inv =>
    inv.Description?.includes('WO Billing') &&
    inv.Description.split(' ')[0]?.toString() === workOrder?.toString()
  );
  if (!match) return { match: null, lineItems: [], total: 0 };

  const lineItems = await getWorkOrderInfoDM(workOrder, match.DueDate, match.Id);
  const total = (lineItems || []).reduce((sum, li) => sum + (parseFloat(String(li.Charge).replace(/[^0-9.-]/g, '')) || 0), 0);
  return { match, lineItems: lineItems || [], total };
}

export async function openWoInvoicePreview(workOrder, customerId, customerName, boatName) {
  const existing = document.getElementById('wo-invoice-preview-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'wo-invoice-preview-modal';
  modal.style.display = 'flex';

  modal.innerHTML = `
    <div class="modal-content wo-invoice-preview-content">
      <div class="button-exit-row">
        <button class="exitButtonSpacer" style="visibility:hidden;"><img src="/assets/close_icon.png" width="24" height="24"></button>
        <h1 id="entryTitle">Invoice preview</h1>
        <button type="button" class="wo-invoice-preview-print-btn" id="wo-invoice-preview-print" disabled>Print / PDF</button>
        <button class="exitButton" data-close="wo-invoice-preview-modal"><img src="/assets/close_icon.png" alt="Close Icon" width="24" height="24"></button>
      </div>
      <div class="wo-invoice-preview-body">
        <div class="wo-invoice-preview-loading">Looking up DockMaster invoice for WO-${escapeHTML(workOrder)}…</div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // The app's generic '.exitButton' click wiring only binds buttons that
  // exist at DOMContentLoaded — this modal is created on demand, long after
  // that, so its own close button needs a direct listener or clicking it
  // silently does nothing (no error, modal just never closes).
  modal.querySelector('[data-close="wo-invoice-preview-modal"]')?.addEventListener('click', () => modal.remove());

  const body = modal.querySelector('.wo-invoice-preview-body');
  const printBtn = modal.querySelector('#wo-invoice-preview-print');

  try {
    const { match, lineItems, total } = await lookupDockmasterInvoiceForWorkOrder(workOrder, customerId);

    if (!match) {
      body.innerHTML = `
        <div class="wo-invoice-preview-header">
          <div><div class="wo-invoice-preview-customer">${escapeHTML(customerName || '')}</div><div class="wo-invoice-preview-boat">${escapeHTML(boatName || '')} · WO-${escapeHTML(workOrder)}</div></div>
        </div>
        <div class="wo-invoice-preview-empty">No DockMaster invoice exists yet for this work order.<br>Invoices are created directly in DockMaster.</div>
      `;
      return;
    }

    body.innerHTML = `
      <div class="wo-invoice-preview-header">
        <div><div class="wo-invoice-preview-customer">${escapeHTML(customerName || '')}</div><div class="wo-invoice-preview-boat">${escapeHTML(boatName || '')} · WO-${escapeHTML(workOrder)}</div></div>
        <span class="wo-invoice-preview-status-pill">Invoice ${match.Id}</span>
      </div>
      <div class="wo-invoice-preview-lines">
        ${(lineItems || []).map(li => `
          <div class="wo-invoice-preview-line">
            <span>${escapeHTML((li.Description || '').split(':')[0])}</span>
            <span>${escapeHTML(li.Charge)}</span>
          </div>
        `).join('') || '<div class="wo-invoice-preview-empty">No line items returned from DockMaster.</div>'}
      </div>
      <div class="wo-invoice-preview-total-row">
        <span>Total</span>
        <span>$${total.toFixed(2)}</span>
      </div>
      <div class="wo-invoice-preview-due">Due ${match.DueDate || '—'}</div>
    `;

    if (printBtn) {
      const dueDate = parseDay(match.DueDate);
      const daysPastDue = isNaN(dueDate) ? 0 : Math.floor((new Date() - dueDate) / 86400000);
      printBtn.disabled = false;
      printBtn.addEventListener('click', () => {
        openInvoiceDocument({
          Id: match.Id,
          Description: match.Description,
          DueDate: match.DueDate,
          InvoiceAmount: match.InvoiceAmount,
          DaysPastDue: daysPastDue,
          WorkOrder: workOrder,
          CustomerName: customerName,
          CustomerId: customerId,
          BoatName: boatName,
          LineItems: lineItems || []
        });
      });
    }
  } catch (err) {
    console.error('Error loading WO invoice preview:', err);
    body.innerHTML = `<div class="wo-invoice-preview-empty">Could not reach DockMaster: ${escapeHTML(err.message)}</div>`;
  }
}

/* function fetchOperationsForWorkOrder(workOrderNumber) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('openDatabase');

    request.onsuccess = (event) => {
      const db = event.target.result;
      const transaction = db.transaction(['jobData'], 'readonly');
      const objectStore = transaction.objectStore('jobData');

      const getRequest = objectStore.getAll();

      getRequest.onsuccess = () => {
        const allJobs = getRequest.result;
        const filteredJobs = allJobs.filter(job => {
          return (
            job.Work_Order === workOrderNumber &&
            job.Category.toString() !== 'Declined'
          );
        });
        resolve(filteredJobs);
      };

      getRequest.onerror = (event) => {
        reject(new Error(`Error fetching operations from IndexedDB: ${event.target.error}`));
      };
    };

    request.onerror = (event) => {
      reject(new Error(`Error opening IndexedDB: ${event.target.error}`));
    };
  });
} */
